import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, MessageCircle, Plus, Share2, Users } from "lucide-react";
import { toast } from "sonner";

import KindredShareSheet from "@/components/KindredShareSheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { buildKindredWebUrl } from "@/lib/kindredShare";
import { formatPublicLocation } from "@/lib/locationPrivacy";
import { getKindredSharePreview } from "@/lib/kindredShare";
import {
  buildActivityChatList,
  type ActivityChatAdventure,
  type ActivityChatListItem,
  type ActivityChatMembership,
  type ActivityChatMessage,
  type ActivityChatUnreadCount,
} from "../../shared/activities";
import {
  applyChannelActivityState,
  applyChatRoomActivityState,
  buildChannelActivityPreviewMap,
  buildRoomActivityPreviewMap,
  getChannelCountryScopeLabel,
  mergeCommunityChatsByRecentActivity,
  normalizeChannelCountryScope,
  parseCommunitySection,
  summarizeDirectMessageConversations,
  type CommunitySectionKey,
  type CommunityActivityPreview,
} from "../../shared/community";
import { orderMembersByLocality } from "../../shared/location";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppInlineAlert,
  AppLoadingState,
  AppSection,
  AppSectionHeader,
  AppSegmentedControl,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

type CommunitySection = CommunitySectionKey;
type ChannelMembership = Pick<Tables<"channel_members">, "channel_id" | "joined_at" | "last_read_at">;
type ChatRoomMembership = Pick<Tables<"chat_room_members">, "room_id" | "joined_at" | "last_read_at">;
type ChannelActivityPost = Pick<Tables<"posts">, "channel_id" | "created_at" | "user_id" | "content"> & {
  profiles?: Pick<Tables<"profiles">, "display_name" | "avatar_url"> | null;
};
type ChannelActivityReply = Pick<Tables<"replies">, "created_at" | "user_id" | "content"> & {
  posts?: Pick<Tables<"posts">, "channel_id"> | null;
  profiles?: Pick<Tables<"profiles">, "display_name" | "avatar_url"> | null;
};
type ChatRoomActivityMessage = Pick<Tables<"chat_room_messages">, "room_id" | "created_at" | "user_id" | "is_deleted" | "content"> & {
  profiles?: Pick<Tables<"profiles">, "display_name" | "avatar_url"> | null;
};
type CommunityChannel = Tables<"channels"> & {
  is_joined?: boolean;
  joined_at?: string | null;
  last_read_at?: string | null;
  last_activity_at?: string | null;
  unread_count?: number;
};
type CommunityRoom = Tables<"chat_rooms"> & {
  is_member?: boolean;
  joined_at?: string | null;
  last_read_at?: string | null;
  last_activity_at?: string | null;
  unread_count?: number;
};
type CommunityConnection = Tables<"connections">;
type NearbyMember = Pick<Tables<"profiles">, "id" | "display_name" | "postcode" | "country_code" | "avatar_url">;
type MessageSnippet = Pick<Tables<"direct_messages">, "id" | "connection_id" | "content" | "created_at" | "read_at" | "sender_id">;
type ConversationProfile = Pick<Tables<"profiles">, "display_name" | "avatar_url" | "postcode" | "country_code">;
type CommunityConversation = CommunityConnection & {
  profile: ConversationProfile | null;
  lastMessage: MessageSnippet | null;
  unreadCount: number;
  unread: boolean;
};

const COMMUNITY_ACTIVITY_PREVIEW_LIMIT = 200;
const DIRECT_MESSAGE_OVERVIEW_LIMIT = 250;

type ChatUnreadCountRow = {
  conversation_id: string;
  conversation_type: string;
  unread_count: number;
};

const fetchActivityChatsForUser = async (
  userId: string,
): Promise<{ activityChats: ActivityChatListItem[]; unreadCounts: ChatUnreadCountRow[] }> => {
  const { data: adventureRows, error: adventuresError } = await supabase
    .from("adventures")
    .select("id, user_id, title, location, date_start, date_end, created_at, chat_last_message_at")
    .eq("status", "active");

  if (adventuresError) {
    console.warn("Could not fetch activity chats", adventuresError);
  }

  const adventures = (adventureRows ?? []) as ActivityChatAdventure[];
  const adventureIds = adventures.map((adventure) => adventure.id);

  if (!adventureIds.length) {
    return { activityChats: [], unreadCounts: [] };
  }

  const [
    { data: memberships, error: membershipsError },
    { data: messages, error: messagesError },
    { data: unreadCounts, error: unreadCountsError },
  ] =
    await Promise.all([
      supabase
        .from("adventure_members")
        .select("adventure_id, user_id, status, created_at")
        .in("adventure_id", adventureIds),
      supabase
        .from("adventure_messages")
        .select("adventure_id, content, created_at, user_id, profiles(display_name)")
        .in("adventure_id", adventureIds)
        .order("created_at", { ascending: false })
        .limit(COMMUNITY_ACTIVITY_PREVIEW_LIMIT),
      supabase.rpc("get_chat_unread_counts"),
    ]);

  if (membershipsError || messagesError || unreadCountsError) {
    console.warn("Could not fetch activity chat summaries", membershipsError ?? messagesError ?? unreadCountsError);
  }

  const typedUnreadCounts = (unreadCounts ?? []) as ChatUnreadCountRow[];
  return {
    activityChats: buildActivityChatList({
      adventures,
      memberships: (memberships ?? []) as ActivityChatMembership[],
      messages: (messages ?? []) as ActivityChatMessage[],
      unreadCounts: typedUnreadCounts.filter(
        (entry) => entry.conversation_type === "activity",
      ) as ActivityChatUnreadCount[],
      currentUserId: userId,
    }),
    unreadCounts: typedUnreadCounts,
  };
};

const parseSection = (value: string | null): CommunitySection => {
  return parseCommunitySection(value);
};

const Communities = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [channels, setChannels] = useState<CommunityChannel[]>([]);
  const [chatRooms, setChatRooms] = useState<CommunityRoom[]>([]);
  const [activityChats, setActivityChats] = useState<ActivityChatListItem[]>([]);
  const [conversations, setConversations] = useState<CommunityConversation[]>([]);
  const [channelPreviewById, setChannelPreviewById] = useState<Record<string, CommunityActivityPreview>>({});
  const [roomPreviewById, setRoomPreviewById] = useState<Record<string, CommunityActivityPreview>>({});
  const [nearbyMembers, setNearbyMembers] = useState<NearbyMember[]>([]);
  const [nearbyMembersError, setNearbyMembersError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [channelActionId, setChannelActionId] = useState<string | null>(null);

  const section = parseSection(searchParams.get("section"));
  const communityChats = useMemo(
    () => mergeCommunityChatsByRecentActivity(chatRooms, activityChats),
    [activityChats, chatRooms],
  );

  const loadCommunities = useCallback(async () => {
    if (!user) return;

    setLoading(true);

    const [
      { data: nextChannels },
      { data: channelMemberships },
      { data: nextRooms },
      { data: roomMemberships },
      { data: connections },
      { data: viewerProfile },
      { data: nextNearbyMembers, error: nearbyError },
      { data: channelActivityPosts },
      { data: channelActivityReplies },
      { data: roomMessages },
      nextActivityChatState,
    ] = await Promise.all([
      supabase.from("channels").select("*").order("sort_order", { ascending: true }),
      supabase.from("channel_members").select("channel_id, joined_at, last_read_at").eq("user_id", user.id),
      supabase.from("chat_rooms").select("*"),
      supabase.from("chat_room_members").select("room_id, joined_at, last_read_at").eq("user_id", user.id),
      supabase
        .from("connections")
        .select("*")
        .eq("status", "connected")
        .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`),
      supabase.from("my_private_profile").select("postcode, country_code").maybeSingle(),
      supabase
        .from("profiles")
        .select("id, display_name, postcode, country_code, avatar_url")
        .neq("id", user.id)
        .limit(60),
      supabase
        .from("posts")
        .select("channel_id, created_at, user_id, content, profiles(display_name, avatar_url)")
        .order("created_at", { ascending: false })
        .limit(COMMUNITY_ACTIVITY_PREVIEW_LIMIT),
      supabase
        .from("replies")
        .select("created_at, user_id, content, profiles(display_name, avatar_url), posts!inner(channel_id)")
        .order("created_at", { ascending: false })
        .limit(COMMUNITY_ACTIVITY_PREVIEW_LIMIT),
      supabase
        .from("chat_room_messages")
        .select("room_id, created_at, user_id, is_deleted, content, profiles(display_name, avatar_url)")
        .eq("is_deleted", false)
        .order("created_at", { ascending: false })
        .limit(COMMUNITY_ACTIVITY_PREVIEW_LIMIT),
      fetchActivityChatsForUser(user.id),
    ]);

    const nextConnectionRows = (connections ?? []) as CommunityConnection[];
    const otherIds = nextConnectionRows.map((connection) =>
      connection.requester_id === user.id ? connection.recipient_id : connection.requester_id,
    );
    const connectionIds = nextConnectionRows.map((connection) => connection.id);

    const [{ data: conversationProfiles }, { data: directMessages }] = await Promise.all([
      otherIds.length
        ? supabase.from("profiles").select("id, display_name, avatar_url, postcode, country_code").in("id", otherIds)
        : Promise.resolve({ data: [] as Array<ConversationProfile & { id: string }> }),
      connectionIds.length
        ? supabase
            .from("direct_messages")
            .select("id, connection_id, content, created_at, read_at, sender_id")
            .in("connection_id", connectionIds)
            .order("created_at", { ascending: false })
            .limit(DIRECT_MESSAGE_OVERVIEW_LIMIT)
        : Promise.resolve({ data: [] as MessageSnippet[] }),
    ]);

    const conversationProfileById = new Map(
      ((conversationProfiles ?? []) as Array<ConversationProfile & { id: string }>).map((profile) => [profile.id, profile]),
    );

    const nextConversations = summarizeDirectMessageConversations(
      nextConnectionRows,
      (directMessages ?? []) as MessageSnippet[],
      user.id,
    ).map(({ connection, lastMessage, unread, unreadCount }) => {
      const otherId = connection.requester_id === user.id ? connection.recipient_id : connection.requester_id;

      return {
        ...connection,
        profile: conversationProfileById.get(otherId) ?? null,
        lastMessage,
        unread,
        unreadCount,
      } satisfies CommunityConversation;
    });

    const channelsWithActivity = applyChannelActivityState(
      (nextChannels ?? []) as CommunityChannel[],
      (channelMemberships ?? []) as ChannelMembership[],
      (channelActivityPosts ?? []) as ChannelActivityPost[],
      (channelActivityReplies ?? []) as ChannelActivityReply[],
      user.id,
    );
    const roomUnreadCountById = new Map(
      nextActivityChatState.unreadCounts
        .filter((entry) => entry.conversation_type === "room")
        .map((entry) => [entry.conversation_id, entry.unread_count]),
    );
    const roomsWithActivity = applyChatRoomActivityState(
      (nextRooms ?? []) as CommunityRoom[],
      (roomMemberships ?? []) as ChatRoomMembership[],
      (roomMessages ?? []) as ChatRoomActivityMessage[],
      user.id,
    ).map((room) => ({
      ...room,
      unread_count: roomUnreadCountById.get(room.id) ?? 0,
    }));
    const nextChannelPreviewById = buildChannelActivityPreviewMap(
      (channelActivityPosts ?? []) as ChannelActivityPost[],
      ((channelActivityReplies ?? []) as ChannelActivityReply[]).map((reply) => ({
        ...reply,
        posts: Array.isArray(reply.posts) ? reply.posts[0] ?? null : reply.posts,
      })),
    );
    const nextRoomPreviewById = buildRoomActivityPreviewMap(
      (roomMessages ?? []) as ChatRoomActivityMessage[],
    );

    setChannels(channelsWithActivity);
    setChatRooms(roomsWithActivity);
    setActivityChats(nextActivityChatState.activityChats);
    setConversations(nextConversations);
    setChannelPreviewById(nextChannelPreviewById);
    setRoomPreviewById(nextRoomPreviewById);
    setNearbyMembers(orderMembersByLocality((nextNearbyMembers ?? []) as NearbyMember[], viewerProfile ?? null).slice(0, 6));
    setNearbyMembersError(Boolean(nearbyError));
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (!user) return;

    void loadCommunities();

    const channel = supabase
      .channel(`communities-shell-${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "channels" }, () => void loadCommunities())
      .on("postgres_changes", { event: "*", schema: "public", table: "chat_rooms" }, () => void loadCommunities())
      .on("postgres_changes", { event: "*", schema: "public", table: "chat_room_messages" }, () => void loadCommunities())
      .on("postgres_changes", { event: "*", schema: "public", table: "direct_messages" }, () => void loadCommunities())
      .on("postgres_changes", { event: "*", schema: "public", table: "connections" }, () => void loadCommunities())
      .on("postgres_changes", { event: "*", schema: "public", table: "posts" }, () => void loadCommunities())
      .on("postgres_changes", { event: "*", schema: "public", table: "replies" }, () => void loadCommunities())
      .on("postgres_changes", { event: "*", schema: "public", table: "channel_members", filter: `user_id=eq.${user.id}` }, () => void loadCommunities())
      .on("postgres_changes", { event: "*", schema: "public", table: "adventures" }, () => void loadCommunities())
      .on("postgres_changes", { event: "*", schema: "public", table: "adventure_members" }, () => void loadCommunities())
      .on("postgres_changes", { event: "*", schema: "public", table: "adventure_messages" }, () => void loadCommunities())
      .on("postgres_changes", { event: "*", schema: "public", table: "adventure_chat_reads", filter: `user_id=eq.${user.id}` }, () => void loadCommunities())
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadCommunities, user]);

  const handleSectionChange = (value: string) => {
    const nextSection = parseSection(value);
    setSearchParams(nextSection === "rooms" ? {} : { section: nextSection });
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffHours = (now.getTime() - date.getTime()) / 3600000;

    if (diffHours < 1) return `${Math.max(1, Math.floor(diffHours * 60))}m`;
    if (diffHours < 24) return `${Math.floor(diffHours)}h`;
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  const formatActivityPreview = (preview: CommunityActivityPreview | undefined, fallback: string) => {
    if (!preview?.content) {
      return fallback;
    }

    const base = getKindredSharePreview(preview.content) || fallback;
    return preview.actorName ? `${preview.actorName}: ${base}` : base;
  };

  const formatChannelActivitySummary = (channel: CommunityChannel, isJoined: boolean) => {
    const parts: string[] = [];

    if (isJoined && channel.unread_count) {
      parts.push(`${channel.unread_count} new`);
    }

    if (channel.last_activity_at) {
      parts.push(`Active ${formatTime(channel.last_activity_at)}`);
    } else if (isJoined && channel.joined_at) {
      parts.push(`Joined ${new Date(channel.joined_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`);
    }

    if (!parts.length) {
      parts.push(isJoined ? "Ready whenever you are" : "Open to explore");
    }

    return parts.join(" · ");
  };

  const formatRoomActivitySummary = (room: CommunityRoom) => {
    const parts: string[] = [];

    if (room.unread_count) {
      parts.push(`${room.unread_count} new`);
    }

    if (room.last_activity_at) {
      parts.push(`Active ${formatTime(room.last_activity_at)}`);
    }

    if (typeof room.member_count === "number") {
      parts.push(`${room.member_count} member${room.member_count === 1 ? "" : "s"}`);
    }

    return parts.join(" · ") || "Drop in when you feel ready";
  };

  const formatConversationSubtitle = (conversation: CommunityConversation) => {
    if (conversation.lastMessage) {
      const preview = getKindredSharePreview(conversation.lastMessage.content) || "Sent a message";
      return conversation.lastMessage.sender_id === user?.id ? `You: ${preview}` : preview;
    }

    if (conversation.profile?.postcode) {
      return `Near ${formatPublicLocation(conversation.profile.postcode, conversation.profile.country_code)} · Start gently when you're ready`;
    }

    return "Connected and ready whenever you want to say hello.";
  };

  const formatConversationMeta = (conversation: CommunityConversation) => {
    const latestActivityAt = conversation.lastMessage?.created_at ?? conversation.last_message_at;
    if (latestActivityAt) {
      return formatTime(latestActivityAt);
    }

    return conversation.created_at ? `Connected ${formatTime(conversation.created_at)}` : null;
  };

  const formatInboxSummary = () => {
    const unreadMessageCount = conversations.reduce((total, conversation) => total + conversation.unreadCount, 0);
    if (!conversations.length) {
      return "Your inbox is ready when you are. Start with one nearby member and take it one conversation at a time.";
    }

    if (!unreadMessageCount) {
      return `${conversations.length} conversation${conversations.length === 1 ? "" : "s"} ready whenever you have the headspace to check back in.`;
    }

    return `${unreadMessageCount} unread message${unreadMessageCount === 1 ? "" : "s"} across ${conversations.length} conversation${conversations.length === 1 ? "" : "s"}, waiting when you have room for them.`;
  };

  const buildChannelSharePayload = (channel: CommunityChannel) => ({
    version: 1 as const,
    type: "channel" as const,
    targetId: channel.id,
    title: `${channel.emoji} ${channel.name}`.trim(),
    subtitle: channel.description || "Community channel",
    imageUrl: null,
    route: `/community/channel/${channel.id}`,
    externalUrl: buildKindredWebUrl(`/community/channel/${channel.id}`),
    accessScope: "public" as const,
  });

  const buildRoomSharePayload = (room: CommunityRoom) => ({
    version: 1 as const,
    type: "room" as const,
    targetId: room.id,
    title: room.name,
    subtitle: room.description || `${room.member_count ?? 0} members`,
    imageUrl: null,
    route: `/community/room/${room.id}`,
    externalUrl: buildKindredWebUrl(`/community/room/${room.id}`),
    accessScope: "members" as const,
  });

  const handleJoinChannel = async (channelId: string) => {
    if (!user || channelActionId) return;

    setChannelActionId(channelId);
    const { error } = await supabase.from("channel_members").insert({
      channel_id: channelId,
      user_id: user.id,
    });
    setChannelActionId(null);

    if (error) {
      toast.error(error.message);
      return;
    }

    toast.success("Channel joined");
    await loadCommunities();
  };

  const handleLeaveChannel = async (channel: CommunityChannel) => {
    if (!user || channelActionId) return;

    if (!window.confirm(`Leave ${channel.name}? You can still browse it later.`)) {
      return;
    }

    setChannelActionId(channel.id);
    const { error } = await supabase
      .from("channel_members")
      .delete()
      .eq("channel_id", channel.id)
      .eq("user_id", user.id);
    setChannelActionId(null);

    if (error) {
      toast.error(error.message);
      return;
    }

    toast.success("Channel left");
    await loadCommunities();
  };

  const renderChannelRow = (channel: CommunityChannel, isJoined: boolean) => (
    <AppCard
      key={channel.id}
      backgroundColor={isJoined ? "$secondarySoft" : "$cardBackground"}
      borderColor={isJoined ? "$secondary" : "$borderColor"}
    >
      <AppCardBody
        flexDirection="row"
        alignItems="center"
        gap="$sm"
        $xs={{ flexDirection: "column", alignItems: "stretch" }}
      >
        <button
          type="button"
          onClick={() => navigate(`/community/channel/${channel.id}`)}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <span className="text-xl leading-none">{channel.emoji}</span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <AppText
                fontFamily={kindredFontFamilies.sansBold}
                fontSize="$sm"
                lineHeight="$sm"
                className={`truncate ${channel.unread_count ? "font-bold" : "font-semibold"}`}
              >
                {channel.name}
              </AppText>
              {normalizeChannelCountryScope(channel.country_scope) !== "ALL" ? (
                <span className="inline-flex shrink-0 items-center rounded-full border border-border/70 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                  {getChannelCountryScopeLabel(channel.country_scope)}
                </span>
              ) : null}
              {isJoined && channel.unread_count ? (
                <span className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primaryForeground">
                  {channel.unread_count}
                </span>
              ) : null}
            </div>
            <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" className="line-clamp-2">
              {formatActivityPreview(
                channelPreviewById[channel.id],
                channel.description || (isJoined ? "Catch up on the latest conversation." : "Browse and join when it feels useful."),
              )}
            </AppText>
            <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" className="truncate">
              {formatChannelActivitySummary(channel, isJoined)}
            </AppText>
          </div>
        </button>
        <div className="flex min-w-[58px] shrink-0 flex-col items-end gap-1.5 max-[479px]:w-full max-[479px]:flex-row max-[479px]:items-center max-[479px]:justify-end">
          <KindredShareSheet payload={buildChannelSharePayload(channel)} triggerContent={<Share2 size={16} />} iconOnly />
          <span
            className={`inline-flex items-center justify-center rounded-full border px-2.5 py-1 text-[10px] font-semibold ${
              isJoined
                ? "border-secondary text-secondary"
                : "border-primary text-primary"
            }`}
          >
            {isJoined ? "Joined" : "Discover"}
          </span>
          <AppButton
            tone={isJoined ? "outlinePrimary" : "outlineSecondary"}
            size="xs"
            minWidth={56}
            onPress={() => void (isJoined ? handleLeaveChannel(channel) : handleJoinChannel(channel.id))}
            disabled={channelActionId === channel.id}
          >
            {channelActionId === channel.id ? "Saving..." : isJoined ? "Leave" : "Join"}
          </AppButton>
        </div>
      </AppCardBody>
    </AppCard>
  );

  if (loading) {
    return <AppLoadingState title="Loading communities" body="Gathering channels, rooms, and conversations." />;
  }

  return (
    <AppSection pt="$xs" pb="$sm">
      <AppCard>
        <AppCardBody gap="$sm">
          <AppSectionHeader
            title="Community, at your pace"
            subtitle="Chat rooms, channels, and messages for real connection, without pressure to rush in."
          />
          <AppButtonRow>
            <AppButton tone="outline" size="sm" onPress={() => navigate("/community/local")}>
              Local hub
            </AppButton>
            <AppButton tone="outline" size="sm" onPress={() => navigate("/community/members")}>
              Find members
            </AppButton>
          </AppButtonRow>
          <AppSegmentedControl
            pillItems
            value={section}
            onChange={handleSectionChange}
            options={[
              { value: "rooms", label: "Chat Rooms", detail: chatRooms.length + activityChats.length },
              { value: "channels", label: "Channels", detail: channels.length },
              { value: "messages", label: "Messages", detail: conversations.length },
            ]}
          />
        </AppCardBody>
      </AppCard>

      {section === "channels" ? (
        <AppSection gap="$sm">
          <AppSectionHeader title="Channels" subtitle="Conversations you can browse and join at your own pace." />
          <AppSection gap="$xs">
            {channels.length ? (
              channels.map((channel) => renderChannelRow(channel, Boolean(channel.is_joined)))
            ) : (
              <AppEmptyState
                title="No channels yet"
                body="Channels will appear here as they are created."
              />
            )}
          </AppSection>
        </AppSection>
      ) : null}

      {section === "rooms" ? (
        <AppSection gap="$sm">
          <AppSectionHeader
            title="Chat rooms"
            subtitle="Smaller group conversations that are easier to dip into without pressure."
            action={
              <AppButton tone="outline" size="sm" onPress={() => navigate("/community/room/new")}>
                <Plus size={16} /> Create room
              </AppButton>
            }
          />

          {chatRooms.length === 0 && activityChats.length === 0 ? (
            <AppEmptyState
              title="No chat rooms yet"
              body="Create the first room when there is a topic people can gather around gently."
              action={
                <AppButton tone="outline" size="sm" onPress={() => navigate("/community/room/new")}>
                  Create the first one
                </AppButton>
              }
            />
          ) : (
            <AppSection gap="$xs">
              {communityChats.map((entry) => {
                if (entry.kind === "activity") {
                  const activityChat = entry.item;
                  const isJoined = activityChat.isOrganiser || activityChat.isJoined;

                  return (
                    <AppCard
                      key={`activity-${activityChat.id}`}
                      backgroundColor={isJoined ? "$secondarySoft" : "$cardBackground"}
                      borderColor={isJoined ? "$secondary" : "$borderColor"}
                    >
                      <AppCardBody
                        flexDirection="row"
                        alignItems="center"
                        gap="$sm"
                        $xs={{ flexDirection: "column", alignItems: "stretch" }}
                      >
                        <button
                          type="button"
                          onClick={() => navigate(`/adventures/${activityChat.id}/chat`)}
                          className="flex min-w-0 flex-1 items-center gap-3 text-left"
                        >
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                            <MessageCircle size={18} className="text-primary" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start gap-2">
                              <AppText
                                fontFamily={kindredFontFamilies.sansBold}
                                fontSize="$sm"
                                lineHeight="$sm"
                                className={`line-clamp-2 font-semibold ${activityChat.unreadCount ? "font-bold" : ""}`}
                              >
                                {activityChat.title}
                              </AppText>
                              {activityChat.unreadCount ? (
                                <span className="inline-flex min-w-[20px] shrink-0 items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primaryForeground">
                                  {activityChat.unreadCount}
                                </span>
                              ) : null}
                            </div>
                            <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" className="line-clamp-2">
                              {activityChat.preview}
                            </AppText>
                            <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                              {activityChat.isOrganiser ? "Organising" : activityChat.isJoined ? "Going" : "Open activity"} · {activityChat.memberCount} member{activityChat.memberCount === 1 ? "" : "s"}
                              {activityChat.lastActivityAt ? ` · Active ${formatTime(activityChat.lastActivityAt)}` : ""}
                            </AppText>
                          </div>
                        </button>
                        <div className="flex shrink-0 flex-col items-end gap-1.5 max-[479px]:w-full max-[479px]:flex-row max-[479px]:items-center max-[479px]:justify-end">
                          <span className={`inline-flex items-center justify-center rounded-full border px-2.5 py-1 text-[10px] font-semibold ${isJoined ? "border-secondary text-secondary" : "border-primary text-primary"}`}>
                            {isJoined ? "Joined" : "Open"}
                          </span>
                          <ChevronRight size={18} className="text-muted-foreground" />
                        </div>
                      </AppCardBody>
                    </AppCard>
                  );
                }

                const room = entry.item;
                return (
                  <AppCard
                    key={room.id}
                    backgroundColor={room.is_member ? "$secondarySoft" : "$cardBackground"}
                    borderColor={room.is_member ? "$secondary" : "$borderColor"}
                  >
                    <AppCardBody
                      flexDirection="row"
                      alignItems="center"
                      gap="$sm"
                      $xs={{ flexDirection: "column", alignItems: "stretch" }}
                    >
                      <button
                        type="button"
                        onClick={() => navigate(`/community/room/${room.id}`)}
                        className="flex min-w-0 flex-1 items-center gap-3 text-left"
                      >
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary/10">
                          <Users size={18} className="text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <AppText
                              fontFamily={kindredFontFamilies.sansBold}
                              fontSize="$sm"
                              lineHeight="$sm"
                              className={`truncate ${room.unread_count ? "font-bold" : "font-semibold"}`}
                            >
                              {room.name}
                            </AppText>
                            {room.unread_count ? (
                              <span className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primaryForeground">
                                {room.unread_count}
                              </span>
                            ) : null}
                          </div>
                          <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" className="line-clamp-2">
                            {formatActivityPreview(
                              roomPreviewById[room.id],
                              room.description || "Drop in when this room feels useful.",
                            )}
                          </AppText>
                          <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                            {formatRoomActivitySummary(room)}
                          </AppText>
                        </div>
                      </button>
                      <div className="flex shrink-0 flex-col items-end gap-1.5 max-[479px]:w-full max-[479px]:flex-row max-[479px]:items-center max-[479px]:justify-end">
                        <KindredShareSheet payload={buildRoomSharePayload(room)} triggerContent={<Share2 size={16} />} iconOnly />
                        <span
                          className={`inline-flex items-center justify-center rounded-full border px-2.5 py-1 text-[10px] font-semibold ${
                            room.is_member ? "border-secondary text-secondary" : "border-primary text-primary"
                          }`}
                        >
                          {room.is_member ? "Joined" : "Open"}
                        </span>
                        <ChevronRight size={18} className="text-muted-foreground" />
                      </div>
                    </AppCardBody>
                  </AppCard>
                );
              })}
            </AppSection>
          )}
        </AppSection>
      ) : null}

      {section === "messages" ? (
        <AppSection gap="$sm">
          <AppCard>
            <AppCardBody gap="$sm">
              <AppSectionHeader
                title="Inbox"
                subtitle={formatInboxSummary()}
                action={
                  <AppButton tone="outline" size="sm" onPress={() => navigate("/community/members")}>
                    Find members
                  </AppButton>
                }
              />
            </AppCardBody>
          </AppCard>

          {conversations.length === 0 ? (
            <AppEmptyState
              title="No messages yet"
              body="Start with one nearby member when it feels comfortable. You do not need to rush the first hello."
              action={
                <AppButton tone="outline" size="sm" onPress={() => navigate("/community/members")}>
                  Find members nearby
                </AppButton>
              }
            />
          ) : (
            <AppCard>
              <AppCardBody gap={0}>
                {conversations.map((conversation) => (
                  <button
                    key={conversation.id}
                    onClick={() => navigate(`/messages/${conversation.id}`)}
                    className="flex w-full items-center gap-3 px-3.5 py-2 text-left transition hover:bg-muted/40"
                  >
                    <Avatar className="h-10 w-10 shrink-0">
                      {conversation.profile?.avatar_url ? (
                        <AvatarImage src={conversation.profile.avatar_url} alt={conversation.profile?.display_name ?? "Member"} />
                      ) : null}
                      <AvatarFallback className="bg-primary/20 font-semibold text-primary">
                        {(conversation.profile?.display_name ?? "K")[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className={`truncate text-sm text-foreground ${conversation.unread ? "font-bold" : "font-medium"}`}>
                          {conversation.profile?.display_name ?? "Member"}
                        </span>
                        <div className="flex shrink-0 items-center gap-2">
                          {formatConversationMeta(conversation) ? (
                            <span className="text-xs text-muted-foreground">
                              {formatConversationMeta(conversation)}
                            </span>
                          ) : null}
                          {conversation.unreadCount > 0 ? (
                            <span className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primaryForeground">
                              {conversation.unreadCount}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <p className={`truncate text-xs ${conversation.unread ? "font-medium text-foreground" : "text-muted-foreground"}`}>
                        {formatConversationSubtitle(conversation)}
                      </p>
                    </div>
                    <MessageCircle size={16} className="shrink-0 text-muted-foreground" />
                  </button>
                ))}
              </AppCardBody>
            </AppCard>
          )}

          {nearbyMembersError ? (
            <AppInlineAlert title="Near you is unavailable right now">
              We could not load nearby members for Messages just now. Try again in a moment.
            </AppInlineAlert>
          ) : nearbyMembers.length > 0 ? (
            <AppCard>
                  <AppCardBody gap="$sm">
                    <AppSectionHeader
                      title="Near you"
                      subtitle="People nearby when you feel ready to start with a hello."
                      action={
                    <AppButton tone="primary" size="sm" onPress={() => navigate("/community/members")}>
                      See all
                    </AppButton>
                      }
                    />

                <div className="flex gap-2.5 overflow-x-auto pb-1">
                  {nearbyMembers.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => navigate(`/community/member/${member.id}`)}
                      className="flex w-[72px] shrink-0 flex-col items-center gap-1 rounded-[18px] px-1 py-1"
                    >
                      <Avatar className="h-10 w-10">
                        {member.avatar_url ? <AvatarImage src={member.avatar_url} alt={member.display_name ?? "Member"} /> : null}
                        <AvatarFallback className="bg-primary/20 text-sm font-semibold text-primary">
                          {(member.display_name ?? "K")[0]}
                        </AvatarFallback>
                      </Avatar>
                      <span className="w-full truncate text-center text-[11px] font-medium text-foreground">
                        {member.display_name ?? "Member"}
                      </span>
                      {member.postcode ? (
                        <span className="text-[10px] text-muted-foreground">{formatPublicLocation(member.postcode, member.country_code)}</span>
                      ) : null}
                    </button>
                  ))}
                </div>
              </AppCardBody>
            </AppCard>
          ) : null}
        </AppSection>
      ) : null}
    </AppSection>
  );
};

export default Communities;
